/**
 * filters.ts — ตัวกรองกลางของรายงาน (T083)
 *
 * - ช่วงวันที่ + งวดเปรียบเทียบ + กิจการ/ร้าน + ช่องทาง เก็บใน URL (`#pnl?from=...&to=...`)
 *   → refresh หรือส่งลิงก์ให้คนอื่นแล้วเห็นรายงานชุดเดียวกัน
 * - ทุกหน้า/ทุก export ใช้ค่าชุดนี้ (กติกา: comparison ต้องใช้ filter เดียวกัน — CONTEXT §7)
 */
import { toReportDateKey, shiftDateKey } from '@rrp/shared';

export type ComparisonType = 'prev_day' | 'prev_week' | 'prev_month' | 'prev_year';

export interface ReportFilters {
  from: string;
  to: string;
  compare: ComparisonType;
  /** ช่องทางขาย ('' = ทุกช่องทาง) */
  channelCode: string;
  /** กิจการที่กำลังดู (ตั้งจาก context ตอน bootstrap) */
  businessId: string;
}

export const CHANNEL_OPTIONS: [string, string][] = [
  ['', 'ทุกช่องทาง'],
  ['tiktok_shop', 'TikTok Shop'],
  ['shopee', 'Shopee'],
  ['lazada', 'Lazada'],
  ['facebook', 'Facebook'],
  ['line', 'LINE'],
];

export const COMPARE_OPTIONS: [ComparisonType, string][] = [
  ['prev_month', 'เทียบเดือนก่อน'],
  ['prev_year', 'เทียบปีก่อน'],
  ['prev_week', 'เทียบสัปดาห์ก่อน'],
  ['prev_day', 'เทียบวันก่อน'],
];

/** วันนี้ตามเขตเวลา Asia/Bangkok (รายงานตัดวันด้วยเขตเวลานี้) */
export const today = (): string => toReportDateKey(new Date());

export function monthRange(dateKey: string): { from: string; to: string } {
  const [y, m] = dateKey.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, '0')}` };
}

/** พรีเซ็ตช่วงวันที่ที่ใช้บ่อย */
export function presetRange(preset: string): { from: string; to: string } {
  const t = today();
  switch (preset) {
    case 'today':
      return { from: t, to: t };
    case 'last7':
      return { from: shiftDateKey(t, -6), to: t };
    case 'last30':
      return { from: shiftDateKey(t, -29), to: t };
    case 'this_month':
      return monthRange(t);
    case 'prev_month': {
      const firstOfThis = monthRange(t).from;
      return monthRange(shiftDateKey(firstOfThis, -1));
    }
    case 'this_year':
      return { from: `${t.slice(0, 4)}-01-01`, to: `${t.slice(0, 4)}-12-31` };
    default:
      return monthRange(t);
  }
}

const DEFAULTS: Omit<ReportFilters, 'businessId'> = {
  ...monthRange(today()),
  compare: 'prev_month',
  channelCode: '',
};

let current: ReportFilters = { ...DEFAULTS, businessId: '' };

/** อ่านตัวกรองจาก URL (`#route?from=...`) — ค่าที่ไม่ถูกต้องใช้ค่าเริ่มต้น */
export function readFiltersFromUrl(): Partial<ReportFilters> {
  const hash = location.hash.replace(/^#/, '');
  const qIndex = hash.indexOf('?');
  if (qIndex < 0) return {};
  const params = new URLSearchParams(hash.slice(qIndex + 1));
  const out: Partial<ReportFilters> = {};
  const dateOk = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);
  if (dateOk(params.get('from'))) out.from = params.get('from')!;
  if (dateOk(params.get('to'))) out.to = params.get('to')!;
  const compare = params.get('compare');
  if (compare && COMPARE_OPTIONS.some(([v]) => v === compare)) {
    out.compare = compare as ComparisonType;
  }
  const channel = params.get('channel');
  if (channel !== null && CHANNEL_OPTIONS.some(([v]) => v === channel)) out.channelCode = channel;
  const biz = params.get('biz');
  if (biz) out.businessId = biz;
  return out;
}

/** route ปัจจุบัน (ตัด query ออก) */
export function currentRoute(): string {
  const hash = location.hash.replace(/^#/, '');
  const qIndex = hash.indexOf('?');
  const route = qIndex < 0 ? hash : hash.slice(0, qIndex);
  return route || 'today'; // หน้าแรก = กำไรวันนี้ (โหมดง่าย)
}

export function buildHash(route: string, f: ReportFilters): string {
  const params = new URLSearchParams({ from: f.from, to: f.to, compare: f.compare });
  if (f.channelCode) params.set('channel', f.channelCode);
  if (f.businessId) params.set('biz', f.businessId);
  return `#${route}?${params.toString()}`;
}

export function getFilters(): ReportFilters {
  return current;
}

export function initFilters(defaults: Partial<ReportFilters>): ReportFilters {
  current = { ...current, ...defaults, ...readFiltersFromUrl() };
  if (current.from > current.to) current.to = current.from;
  return current;
}

let listener: (() => void | Promise<void>) | null = null;

/** ให้ main ผูกฟังก์ชันโหลดข้อมูลใหม่เมื่อตัวกรองเปลี่ยน */
export function onFiltersChanged(cb: () => void | Promise<void>): void {
  listener = cb;
}

/**
 * อัปเดตตัวกรอง + เขียนลง URL แล้วสั่งโหลดข้อมูลใหม่
 * ใช้ replaceState เพื่อไม่ให้ hashchange ทำให้ render ซ้ำซ้อน
 */
export function setFilters(patch: Partial<ReportFilters>): ReportFilters {
  current = { ...current, ...patch };
  if (current.from > current.to) current.to = current.from;
  const next = buildHash(currentRoute(), current);
  if (location.hash !== next) history.replaceState(null, '', next);
  void listener?.();
  return current;
}

/**
 * อ่านตัวกรองจาก URL เข้ามาใช้ (กรณีผู้ใช้เปิดลิงก์/กด back-forward)
 * คืน true ถ้ามีค่าที่เปลี่ยนจริง → ผู้เรียกค่อยโหลดข้อมูลใหม่
 */
export function applyUrlFilters(): boolean {
  const fromUrl = readFiltersFromUrl();
  const changed = (Object.keys(fromUrl) as (keyof ReportFilters)[]).some(
    (k) => fromUrl[k] !== undefined && fromUrl[k] !== current[k],
  );
  if (changed) current = { ...current, ...fromUrl };
  return changed;
}

/** เขียน URL ให้ตรงกับตัวกรองปัจจุบัน (เรียกหลังเปลี่ยนหน้า) */
export function syncUrl(): void {
  const next = buildHash(currentRoute(), current);
  if (location.hash !== next) history.replaceState(null, '', next);
}

/** ป้ายช่วงวันที่แบบอ่านง่าย เช่น "1 เม.ย. 2025 – 30 เม.ย. 2025" */
const THAI_MONTHS = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];
export function formatDateThai(dateKey: string): string {
  const [y, m, d] = dateKey.split('-');
  return `${Number(d)} ${THAI_MONTHS[Number(m) - 1] ?? m} ${y}`;
}
export function rangeLabel(f: { from: string; to: string }): string {
  return f.from === f.to
    ? formatDateThai(f.from)
    : `${formatDateThai(f.from)} – ${formatDateThai(f.to)}`;
}
